import { Component, ElementRef, HostBinding, Input, OnDestroy } from "@angular/core";
import { distinctUntilChangedJson } from "@app/_helpers/utils";
import { AppService } from "@app/app.service";
import { APP_ASSIGN_COLOR_SCHEME } from "@app/shared/color-schemes/office-scheme";
import { TranslateService } from "@ngx-translate/core";
import { Logger } from "@timeghost/web-api";
import { ApexOptions } from "apexcharts";
import { merge } from "lodash-es";
import { BehaviorSubject, combineLatest, filter, map } from "rxjs";
import { observeElementWidth } from "../observe-element-width";
import { createReportsBarChartOptions } from "../reports-chart-options";

const log = new Logger("ReportsGraphComponent");

/** Min px per category before stacked bar totals are shown. */
const MIN_PX_PER_BAR_FOR_TOTALS = 55;
/** Approximate opposite y-axis width reserved from host width. */
const Y_AXIS_WIDTH_PX = 70;

@Component({
	selector: "tg-reports-graph",
	templateUrl: "./reports-graph.component.html",
	styleUrls: ["./reports-graph.component.scss"],
})
export class ReportsGraphComponent implements OnDestroy {
	@HostBinding("class.is-drillable")
	@Input()
	drillable = false;

	@HostBinding("attr.title")
	get drillTitle(): string | null {
		return this.drillable ? this.translate.instant("reports.graph.drill-hint") : null;
	}

	private _formatDurationTotals = true;
	chartOptions: ApexOptions = createReportsBarChartOptions({ formatDurationTotals: true });

	@Input()
	set formatDurationTotals(value: boolean) {
		const next = value !== false;
		if (next === this._formatDurationTotals) return;
		this._formatDurationTotals = next;
		this.chartOptions = createReportsBarChartOptions({ formatDurationTotals: next });
		const current = this._data.getValue();
		if (current) this._data.next(current);
	}
	get formatDurationTotals(): boolean {
		return this._formatDurationTotals;
	}

	private readonly _widthResize = observeElementWidth(this.el);

	constructor(
		private appService: AppService,
		private el: ElementRef<HTMLElement>,
		private translate: TranslateService
	) {}

	ngOnDestroy(): void {
		this._widthResize.disconnect();
	}

	private _data = new BehaviorSubject<ApexOptions | null>(null);
	readonly data$ = combineLatest([
		this._data.asObservable().pipe(
			filter((data): data is ApexOptions => !!data),
			distinctUntilChangedJson((opts) => ({
				seriesNames: (opts?.series as Array<{ name?: string }> | undefined)?.map((s) => s.name),
				series: opts?.series,
				categories: (opts?.xaxis as { categories?: unknown })?.categories,
				stackType: opts?.chart?.stackType,
				stacked: opts?.chart?.stacked,
			}))
		),
		this.appService.selectedTheme$.asObservable(),
		this._widthResize.width$,
	]).pipe(
		map(([data, theme, width]) => {
			const isDark = theme === "dark";
			const categories: unknown[] = (data?.xaxis as { categories?: unknown[] })?.categories ?? [];
			const itemCount = categories.length || 1;
			const chartWidth = Math.max(0, width - Y_AXIS_WIDTH_PX);
			const showBarTotals = chartWidth / itemCount >= MIN_PX_PER_BAR_FOR_TOTALS;
			return merge({}, this.chartOptions, data, <Partial<ApexOptions>>{
				colors: [...APP_ASSIGN_COLOR_SCHEME],
				dataLabels: {
					enabled: false,
				},
				theme: {
					mode: isDark ? "dark" : "light",
				},
				yaxis: {
					opposite: true,
					labels: {
						style: {
							fontSize: "0.875rem",
							fontWeight: 600,
							color: "hsl(var(--secondary))",
							fontFamily: "inherit",
						},
					},
				},
				legend: {
					show: false,
					position: "top",
					horizontalAlign: "right",
					fontFamily: "inherit",
					labels: {
						colors: "hsl(var(--muted-foreground))",
					},
					markers: {
						fillColors: ["hsl(var(--muted))"],
					},
					onItemClick: {
						toggleDataSeries: false,
					},
					onItemHover: {
						highlightDataSeries: false,
					},
				},
				grid: {
					borderColor: "hsl(var(--muted))",
					clipMarkers: false,
				},
				plotOptions: {
					bar: {
						dataLabels: {
							total: {
								enabled: showBarTotals,
							},
						},
					},
				},
			});
		}),
		map((data) => {
			log.debug(data);
			return data;
		})
	);
	get data() {
		return this._data.getValue();
	}
	@Input()
	set data(val: ApexOptions | null) {
		this._data.next(val);
	}

	trackChart(_index: number, chart: ApexOptions): string {
		const series = (chart?.series ?? []) as Array<{ name?: string; data?: unknown[] }>;
		const names = series.map((s) => s.name ?? "").join("|");
		const categoryCount = ((chart?.xaxis as { categories?: unknown[] })?.categories ?? []).length;
		return `${names}#${categoryCount}#${series.length}`;
	}
}
